import { Box3, LoadingManager, Mesh, Object3D, Scene, Vector3 } from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { MaterialManager } from '../materials/MaterialManager'
import { disposeObject3D } from '../utils/disposeObject3D'
import type { SceneMetrics } from './BedroomScene'

export type ExteriorInteractionId = 'flowerpot' | 'doorKey' | 'doormat' | 'decoyPot'

const INTERACTION_NODE_NAMES: Readonly<Record<ExteriorInteractionId, readonly string[]>> = {
  flowerpot: ['INTERACT_BrokenFlowerPot'],
  doorKey: ['INTERACT_DoorKey_ROOT'],
  doormat: ['INTERACT_WelcomeDoormat_ROOT'],
  // The fallback name keeps today's GLB plug-and-play. Future exports rename
  // this same authored object to the stable INTERACT node.
  decoyPot: [
    'INTERACT_PorchPlanter',
    'Pottery_Procedual_Pot_Generator',
    'Pottery Procedual Pot Generator',
  ],
}

const isFalse = (value: unknown): boolean => (
  value === false || value === 0 || value === 'false' || value === 'False'
)

export class ExteriorScene {
  private readonly scene: Scene
  private readonly loader: GLTFLoader
  private readonly materialManager: MaterialManager
  private root: Object3D | null = null
  private metrics: SceneMetrics | null = null
  private readonly interactions = new Map<ExteriorInteractionId, Object3D>()

  public constructor(
    scene: Scene,
    loadingManager: LoadingManager,
    materialManager: MaterialManager,
  ) {
    this.scene = scene
    this.loader = new GLTFLoader(loadingManager)
    this.materialManager = materialManager
  }

  /** Load and prepare the exterior while the packed-kitchen dialogue is active. */
  public async prepare(modelUrl: string): Promise<SceneMetrics> {
    if (this.metrics) return this.metrics

    const [, gltf] = await Promise.all([
      this.materialManager.load(),
      this.loader.loadAsync(modelUrl),
    ])

    const root = gltf.scene
    try {
      root.name = 'ElinaExteriorEnvironment'
      this.applyDefaultVisibility(root)
      const materialSummary = this.materialManager.apply(root)
      this.captureInteractions(root)

      let objects = 0
      let meshes = 0
      root.traverse((object) => {
        objects += 1
        if (!(object instanceof Mesh)) return
        meshes += 1
        object.frustumCulled = true
        if (!object.geometry.boundingSphere) object.geometry.computeBoundingSphere()
      })

      root.updateMatrixWorld(true)
      const size = new Box3().setFromObject(root).getSize(new Vector3())
      this.root = root
      this.metrics = { objects, meshes, size, materialSummary }
      return this.metrics
    } catch (error) {
      const disposed = disposeObject3D(root)
      this.materialManager.forgetDisposedMaterials(disposed.materials)
      this.interactions.clear()
      throw error
    }
  }

  public show(): void {
    if (!this.root) throw new Error('ExteriorScene.prepare() must finish before show().')
    if (this.root.parent !== this.scene) this.scene.add(this.root)
    this.root.updateMatrixWorld(true)
  }

  public getInteraction(id: ExteriorInteractionId): Object3D | null {
    return this.interactions.get(id) ?? null
  }

  public setGroupVisible(group: string, visible: boolean): number {
    if (!this.root) return 0
    const normalizedGroupName = group.startsWith('GROUP_') ? group : `GROUP_${group}`
    let matches = 0
    this.root.traverse((object) => {
      if (
        object.userData.web_group !== group
        && object.name !== group
        && object.name !== normalizedGroupName
      ) return
      object.visible = visible
      matches += 1
    })
    return matches
  }

  public listGroups(): string[] {
    if (!this.root) return []
    const groups = new Set<string>()
    this.root.traverse((object) => {
      const group = object.userData.web_group
      if (typeof group === 'string' && group) groups.add(group)
    })
    return [...groups].sort()
  }

  private applyDefaultVisibility(root: Object3D): void {
    root.traverse((object) => {
      if (isFalse(object.userData.web_default_visible)) object.visible = false
    })
  }

  private captureInteractions(root: Object3D): void {
    this.interactions.clear()
    for (const [id, nodeNames] of Object.entries(INTERACTION_NODE_NAMES) as Array<[
      ExteriorInteractionId,
      readonly string[],
    ]>) {
      const object = nodeNames
        .map((nodeName) => root.getObjectByName(nodeName))
        .find((candidate): candidate is Object3D => Boolean(candidate))
      if (!object) {
        throw new Error(`Exterior interaction node "${nodeNames.join('" or "')}" is missing.`)
      }
      this.interactions.set(id, object)
    }
  }

  public dispose(): void {
    if (!this.root) return
    this.scene.remove(this.root)
    const disposed = disposeObject3D(this.root)
    this.materialManager.forgetDisposedMaterials(disposed.materials)
    this.root = null
    this.metrics = null
    this.interactions.clear()
  }
}
